import React, { useState, useCallback, useEffect } from 'react';
import type { KeyboardEvent } from 'react';
import type { NavItem, WhistleProNavigationProps } from './whistlepro-navbar-types';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../common/LanguageSwitcher';
import './whistlepro-navbar.css';
import './whistlepro-navbar-desktop.css';


export const WhistleProNavigation: React.FC<WhistleProNavigationProps> = ({
  onNavChange,
  onGetStarted,
  defaultActiveNav = 'dashboard',
  className = '',
  onLogoClick,
  showThemeToggle = true,
  theme = 'light',
  onThemeToggle,
}) => {
  const { t } = useTranslation();
  
  const navItems: NavItem[] = [
    {
      id: 'dashboard',
      title: t('nav.home'),
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
        </svg>
      ),
    },
    {
      id: 'pricing',
      title: t('nav.pricing', 'Pricing'),
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1.93.66 1.64 2.08 1.64 1.51 0 2.1-.86 2.1-1.69 0-1.02-.53-1.64-2.24-2.1-2.06-.56-3.38-1.5-3.38-3.45 0-1.67 1.16-2.84 2.76-3.23V4h2.66v1.86c1.63.38 2.74 1.45 2.85 3.15h-1.95c-.13-.8-.6-1.42-1.76-1.42-1.17 0-1.93.58-1.93 1.61 0 .9.55 1.43 2.08 1.89 2.1.58 3.52 1.4 3.52 3.55-.01 1.93-1.25 3.09-3.1 3.45z"/>
        </svg>
      ),
    },
    {
      id: 'analytics',
      title: t('nav.caseLookup'),
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M10 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2h-8l-2-2z"/>
        </svg>
      ),
    },
    {
      id: 'settings',
      title: t('nav.login'),
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
        </svg>
      ),
    },
  ];
  const [activeNav, setActiveNav] = useState(defaultActiveNav);
  const [isLoading, setIsLoading] = useState(false);
  const [buttonText, setButtonText] = useState(t('nav.getStarted', 'Get Started'));
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [buttonState, setButtonState] = useState<'default' | 'loading' | 'success'>('default');
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [logoError, setLogoError] = useState(false);

  // Sync activeNav with defaultActiveNav prop changes
  useEffect(() => {
    setActiveNav(defaultActiveNav);
  }, [defaultActiveNav]);

  // Preload logos for better performance
  useEffect(() => {
    const lightLogo = new Image();
    const darkLogo = new Image();
    
    lightLogo.src = "/wp-high-resolution-logo-transparent.svg";
    darkLogo.src = "/wp-high-resolution-logo-darkmode.svg";
    
  }, [theme]);

  const handleNavClick = useCallback((navId: string) => {
    setActiveNav(navId);
    onNavChange?.(navId);
  }, [onNavChange]);

  const handleNavKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>, navId: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleNavClick(navId);
    }
  }, [handleNavClick]);

  const handleStartClick = useCallback(async () => {
    if (isLoading) return;

    setIsLoading(true);
    setButtonState('loading');

    try {
      // Call the provided callback if it exists
      if (onGetStarted) {
        await onGetStarted();
      } else {
        // Simulate async operation
        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      setIsLoading(false);
      setButtonState('success');
      setButtonText(t('nav.welcome', 'Welcome!'));
      
      // Reset after 2 seconds
      setTimeout(() => {
        setButtonText(t('nav.getStarted', 'Get Started'));
        setButtonState('default');
      }, 2000);
    } catch (error) {
      console.error('Error in handleStartClick:', error);
      setIsLoading(false);
      setButtonState('default');
      setButtonText(t('common.retry', 'Try Again'));
      
      setTimeout(() => {
        setButtonText(t('nav.getStarted', 'Get Started'));
      }, 2000);
    }
  }, [isLoading, onGetStarted]);

  return (
    <div className="whistlepro-navbar-wrapper">
      <nav className={`whistlepro-navbar ${className}`}>
        <div 
          className="whistlepro-logo" 
          tabIndex={0}
          onClick={onLogoClick}
          style={{ cursor: onLogoClick ? 'pointer' : 'default' }}
        >
          <img 
            src={theme === 'dark' ? "/wp-high-resolution-logo-darkmode.svg" : "/wp-high-resolution-logo-transparent.svg"} 
            alt={t('brand.name', 'WhistlePro')} 
            className={`whistlepro-logo-img ${logoLoaded ? 'loaded' : 'loading'} ${logoError ? 'error' : ''}`}
            onLoad={() => setLogoLoaded(true)}
            onError={() => {
              setLogoError(true);
            }}
          />
        </div>
        
        <div className="whistlepro-nav-center">
          {navItems.map((item) => (
            <div
              key={item.id}
              className={`whistlepro-nav-item-wrapper ${activeNav === item.id ? 'active' : ''}`}
              title={item.title}
              tabIndex={0}
              onClick={() => handleNavClick(item.id)}
              onKeyDown={(e) => handleNavKeyDown(e, item.id)}
              role="button"
              aria-label={item.title}
              aria-current={activeNav === item.id ? 'page' : undefined}
            >
              <div className="whistlepro-nav-item-bg" />
              <div className="whistlepro-nav-item-content">
                <div className="whistlepro-nav-icon">
                  {item.icon}
                </div>
                <span className="whistlepro-nav-text">{item.title}</span>
              </div>
            </div>
          ))}
        </div>
        
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          
          {showThemeToggle && (
            <button
              className="whistlepro-theme-toggle"
              onClick={onThemeToggle}
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              {theme === 'light' ? '🌙' : '☀️'}
            </button>
          )}
          
          <button
          className={`whistlepro-start-button ${buttonState}`}
          onClick={handleStartClick}
          onMouseEnter={() => setIsButtonHovered(true)}
          onMouseLeave={() => setIsButtonHovered(false)}
          aria-busy={isLoading}
          disabled={isLoading}
        >
          <span className={`whistlepro-start-text ${isLoading ? 'hide' : ''}`}>
            {buttonText === 'Get Started' ? t('nav.getStarted', 'Get Started') : buttonText === 'Welcome!' ? t('nav.welcome', 'Welcome!') : buttonText === 'Try Again' ? t('common.retry', 'Try Again') : buttonText}
          </span>
          <div className={`whistlepro-loading-dots ${isLoading ? 'show' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </div>
          <span className={`whistlepro-arrow ${isButtonHovered ? 'hover' : ''}`}>
            →
          </span>
        </button>
        </div>
      </nav>
    </div>
  );
};

export default WhistleProNavigation;